import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot } from "vue"

import { useBreakpoints } from "@/code/useBreakpoints";
import { PropType, onMounted, reactive, ref, watch } from "vue";
import queryString from "query-string";
import { SearchRequest, SearchResponse, Sorting } from "@/models/Search";
import { useAsyncSpinner } from "@/services/Spinner";
import { DataLayerApi, ProductApi } from "@/services/Api";
import { ProductTile } from "@/models/ProductList";
import { useDebounceFn, useResizeObserver } from "@vueuse/core";
import { useCart } from "@/store/useCart";
import { FilterValueList } from "@/store/useFilterState";
import { useUmbracoForm } from "@/store/useUmbracoForm";
import { getCategoryProducts } from "@/code/GoogleEventTracking";
import { FilterService } from "@/services/FilterService";

type UrlSearchParams = {
  page: number | undefined;
  sorting: Sorting | undefined;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'CategoryWrapper',
  props: {
  code: {
    type: String,
    required: true,
  },
  remainingProducts: {
    type: Number,
    required: true,
  },
  totalHits: {
    type: Number,
    required: true,
  },
  initialFacet: {
    type: String,
    required: false,
    default: "",
  },
  showOnlyAgreementProducts: {
    type: Boolean,
    required: true,
  },
  requestForFileFormId: {
    type: String,
    required: true,
  },
  requestForPriceFormId: {
    type: String,
    required: true,
  },
  contentId: {
    type: String,
    required: true,
  },
  availableFilters: {
    type: Object as PropType<Record<string, FilterValueList>>,
    required: true,
  },
},
  setup(__props) {

const props = __props;



const filterService = new FilterService(props.code, props.availableFilters);

const cartStore = useCart();
const umbracoForms = useUmbracoForm();

const SortingDefault = Sorting.RelevanceDesc;
const initialProducts = getCategoryProducts();

const updateUrlHistory = (request: SearchRequest): void => {
  urlSearchParams.page = request.pageNo > 1 ? request.pageNo : undefined;
  urlSearchParams.sorting = request.sorting !== SortingDefault ? request.sorting : undefined;
  const urlQueryString = queryString.stringify(urlSearchParams);
  let url = new URL(location.href).pathname;
  if (urlQueryString) {
    url += "?" + urlQueryString;
  }
  window.history.pushState(structuredClone(request), "", url);
};

const { isMd } = useBreakpoints();
const window2 = ref(window);
const onMoveToTopClick = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const loading = ref<boolean>();
const products = ref<Array<ProductTile>>([]);
const response = ref<SearchResponse>();
const remainingProducts = ref<number>(props.remainingProducts);
const totalHits = ref<number>(props.totalHits);
const showOnlyAgreementProducts = ref<boolean>(props.showOnlyAgreementProducts);

const showMore = async (): Promise<void> => {
  await changePage();
};

const onCartClick = async (partNo: string, quantity: number): Promise<void> => {
  await useAsyncSpinner(async () => {
    await cartStore.addToCart(partNo, quantity, true);
    if (typeof window !== "undefined" && window.BiTracking) {
      const row = cartStore.rows.find((r) => r.item.partNo === partNo);
      window.BiTracking.addToBasket(partNo, row?.quantity, "", cartStore.id, "");
    }
  });
};

const favorited = ref<Array<string>>([]);
const isFavorited = (id: string) => {
  return favorited.value.find((x) => x == id);
};
const onFavoriteClick = (id: string) => {
  const val = favorited.value.find((x) => x == id);
  if (val) {
    favorited.value = favorited.value.filter((x) => x != id);
  } else {
    favorited.value.push(id);
  }
};

const filters = ref(filterService.loadSelectedFilters());

const isFilterOpen = ref<number | string>(0);
const onOpenFilterClick = () => {
  const filterEl = document.getElementById("filters__middle-container");

  if (isFilterOpen.value == 0) {
    if (filterEl) {
      isFilterOpen.value = `${filterEl.scrollHeight}px`;
      setTimeout(() => {
        isFilterOpen.value = "auto";
      }, 300);
    }
  } else {
    if (filterEl) {
      isFilterOpen.value = `${filterEl.scrollHeight}px`;
      setTimeout(() => {
        isFilterOpen.value = "0";
      }, 10);
    }
  }
};

const recalculateFilter = () => {
  const filterEl = document.getElementById("filters__middle-container");
  if (filterEl) {
    isFilterOpen.value = filterEl.scrollHeight;
  }
};

const onToggleFilterClick = (categoryId: string, id: string) => {
  let category = getFilterCategory(categoryId);
  const val = category.find((x) => x == id);
  if (val) {
    category = category.filter((x) => x != id);
  } else {
    category.push(id.toString());
    filters.value[categoryId] = category;
  }
  filters.value[categoryId] = category;
  currentPage.value = 0;
  hideInitialProducts.value = true;
  products.value = [];
  debouncedChangePage(true);
  filterService.updateFilters(filters.value);
};

const onRemoveFilterClick = (categoryId: string, id: string) => {
  let category = getFilterCategory(categoryId);
  category = category.filter((x) => x != id);
  filters.value[categoryId] = category;
  currentPage.value = 0;
  products.value = [];
  debouncedChangePage(true);
  filterService.updateFilters(filters.value);
};
const onRemoveAllFiltersClick = () => {
  filters.value = {};
  currentPage.value = 0;
  products.value = [];
  debouncedChangePage(true);
  filterService.updateFilters(filters.value);
};

const getFilterCategory = (categoryId: string): FilterValueList => {
  let category = filters.value[categoryId] as FilterValueList;
  if (!category) {
    category = [];
    filters.value[categoryId] = category;
  }
  return category;
};

const isFilterCategoryOpen = ref<Array<string>>([]);
const onToggleShowAllFilterCategory = (id: string) => {
  const val = isFilterCategoryOpen.value.find((x) => x == id);
  if (val) {
    isFilterCategoryOpen.value = isFilterCategoryOpen.value.filter((x) => x != id);
  } else {
    isFilterCategoryOpen.value.push(id);
  }
  setTimeout(recalculateFilter, 1);
};

const categoryReadMoreHeight = ref<number>(0);

const onToggleCategoryReadMore = () => {
  const readMoreId = isMd.value ? "category-read-more" : "category-read-more2";
  let contentEl = document.getElementById(readMoreId);
  if (contentEl) {
    if (categoryReadMoreHeight.value === 0) {
      categoryReadMoreHeight.value = contentEl.scrollHeight;
    } else {
      categoryReadMoreHeight.value = 0;
    }
  }
};
watch(isMd, () => {
  if (categoryReadMoreHeight.value != 0) {
    onToggleCategoryReadMore();
  }
});

const hideInitialProducts = ref<boolean>(false);

const onSortChange = (event) => {
  sorting.value = event.target.value;
  currentPage.value = 0;
  changePage();
};

const categoryCodes = Array<string>(props.code);
const requestFullResult = ref<boolean>(false);

const changePage = async (goForward = true): Promise<void> => {
  loading.value = true;
  currentPage.value++;
  const request: SearchRequest = {
    categories: categoryCodes,
    selectedFacets: filters.value ?? null,
    term: null,
    pageNo: currentPage.value,
    sorting: sorting.value,
    requestFullResult: requestFullResult.value,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
    contentId: props.contentId,
  };

  goForward && updateUrlHistory(request);

  await useAsyncSpinner(async () => {
    response.value = await ProductApi.categorySearch(request);

    if (requestFullResult.value || currentPage.value == 1) {
      hideInitialProducts.value = true;
      products.value = response.value.tiles;
      DataLayerApi.createViewItemListEvent("category", Array.from(new Set(products.value.map((x) => x.sku))));
    } else {
      products.value?.push(...response.value.tiles);
      if (hideInitialProducts.value) {
        DataLayerApi.createViewItemListEvent(
          "category",
          products.value.map((x) => x.sku)
        );
      } else {
        DataLayerApi.createViewItemListEvent("category", products.value.map((x) => x.sku).concat(initialProducts));
      }
    }
    updateFilters(response.value?.facets);
    remainingProducts.value = response.value?.remainingProducts;
    totalHits.value = response.value?.totalHits;
    loading.value = false;
    requestFullResult.value = false;
  });
};

const reloadPage = async (pageNo: number): Promise<void> => {
  loading.value = true;
  const request: SearchRequest = {
    contentId: props.contentId,
    categories: categoryCodes,
    selectedFacets: filters.value ?? null,
    term: null,
    pageNo: pageNo,
    sorting: sorting.value,
    requestFullResult: true,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
  };

  await useAsyncSpinner(async () => {
    response.value = await ProductApi.categorySearch(request);
    hideInitialProducts.value = true;
    products.value = response.value.tiles;
    updateFilters(response.value?.facets);
    remainingProducts.value = response.value?.remainingProducts;
    totalHits.value = response.value?.totalHits;
    loading.value = false;
  });
};

const debouncedChangePage = useDebounceFn(changePage, 500);

const updateFilters = (facets: Array<any>) => {
  const groups = document.querySelectorAll(".filter-group");
  for (const group of groups) {
    let facetGroup = facets?.find((x) => x.key == group.id);
    if (!facetGroup) {
      facetGroup = facets?.find((x) => `${x.key}-2` == group.id);
    }
    const filters = [...group.querySelectorAll(".filter")];
    for (const filter of filters) {
      const filterId = filter.id.replace(`${group.id}-`, "").replace("2-", "");
      const facetValue = facetGroup?.available.find((x) => x.value == filterId);
      if (facetValue) {
        if (filters.filter((x) => !x.classList.contains("d-none")).length < 8 || facetGroup.key == "Filter:Color") {
          filter.classList.remove("d-none");
        }
        if (facetGroup.key != "Filter:Color") {
          const filterValueEl = filter;
          if (filterValueEl) {
            filterValueEl.textContent = `${facetValue.value} (${facetValue.hits})`;
          }
        }
      } else {
        filter.classList.add("d-none");
      }
    }
    const showAllButtonEl = group.querySelector(".filters__show-all-button");
    if (showAllButtonEl) {
      const hiddenFilters = filters.filter((x) => x.classList.contains("d-none"));
      if (filters.length - hiddenFilters.length < 8) {
        showAllButtonEl.classList.add("d-none");
      } else {
        showAllButtonEl.classList.remove("d-none");
      }
    }
  }
};
const isFiltersMenuOpen = ref<boolean>(false);
const openFiltersMenu = () => {
  isFiltersMenuOpen.value = true;
  openFilterAccordion(props.initialFacet);
};
const openFiltersMenu2 = () => {
  isFiltersMenuOpen.value = true;
};
const closeFiltersMenu = () => {
  isFiltersMenuOpen.value = false;
  isSortOpen.value = false;
};
const onFiltersMenuCloseClick = () => {
  closeFiltersMenu();
};
const onFiltersModalClick = (ev) => {
  if (ev.target.classList.contains("filters-modal-main")) {
    closeFiltersMenu();
  }
};
const onFiltersMenuOpenClick = () => {
  openFiltersMenu();
};
const onFiltersMenuOpenClick2 = (id: string) => {
  openFiltersMenu2();
  openFilterAccordion(id);
};
const toggleDisplayMode = () => {
  showOnlyAgreementProducts.value = !showOnlyAgreementProducts.value;
  currentPage.value = 0;
  changePage(true);
};

const filtersModalAccordionId = ref<string>();
const filtersModalAccordionHeight = ref<number>(0);

const openFilterAccordion = (id: string) => {
  filtersModalAccordionId.value = id;
  const contentEl = document
    .getElementById(filtersModalAccordionId.value)
    ?.querySelector(".filters-modal__accordion__content");
  if (contentEl) {
    filtersModalAccordionHeight.value = contentEl.scrollHeight;
  }
};

const onToggleFilterAccordion = (id: string) => {
  if (id == filtersModalAccordionId.value) {
    filtersModalAccordionId.value = undefined;
    filtersModalAccordionHeight.value = 0;
  } else {
    filtersModalAccordionId.value = id;
    const contentEl = document.getElementById(id)?.querySelector(".filters-modal__accordion__content");
    if (contentEl) {
      filtersModalAccordionHeight.value = contentEl.scrollHeight;
    }
  }
};

useResizeObserver(document.body, () => {
  if (filtersModalAccordionId.value) {
    const contentEl = document
      .getElementById(filtersModalAccordionId.value)
      ?.querySelector(".filters-modal__accordion__content");
    if (contentEl) {
      filtersModalAccordionHeight.value = contentEl.scrollHeight;
    }
  }
});
const isSortOpen = ref<boolean>(false);
const isSortOpen2 = ref<boolean>(false);
const openSort = () => {
  isSortOpen.value = true;
};
const closeSort = () => {
  isSortOpen.value = false;
};
const openSort2 = () => {
  isSortOpen2.value = true;
};
const closeSort2 = () => {
  isSortOpen2.value = false;
};
const onOpenSortClick = () => {
  openSort();
};
const onCloseSortClick = () => {
  closeSort();
};
const onToggleSortClick = () => {
  if (isSortOpen2.value) {
    closeSort2();
  } else {
    openSort2();
  }
};
const onSortChangeDesktop = ($ev) => {
  closeSort2();
  onSortChange($ev);
};
const _hasFiltersValue = () => {
  return (
    Object.keys(filters.value)
      .map((x) => filters.value[x].length > 0)
      .indexOf(true) > -1
  );
};

const createUrlSearchParams = (url: string): UrlSearchParams => {
  const query = queryString.parse(url);
  return {
    page: query.page ? parseInt(query.page.toString(), 10) : undefined,
    sorting: query.sorting as Sorting,
  };
};

let urlSearchParams = reactive<UrlSearchParams>(createUrlSearchParams(location.search));
const currentPage = ref<number>(urlSearchParams.page ?? 1);
const sorting = ref<Sorting>(urlSearchParams.sorting ?? SortingDefault);

const loadUmbracoForms = () => {
  if (props.requestForFileFormId) {
    umbracoForms.get(props.requestForFileFormId);
  }

  if (props.requestForPriceFormId) {
    umbracoForms.get(props.requestForPriceFormId);
  }
};

onMounted(async () => {
  if (filters.value && Object.keys(filters.value).length > 0 && _hasFiltersValue()) {
    hideInitialProducts.value = true;
    await reloadPage(currentPage.value);
  }

  toggleElementDisplayOnMounted();
  loadUmbracoForms();
});

window.addEventListener("popstate", async (event: PopStateEvent) => {
  const request = event.state as SearchRequest;
  if (request) {
    const isForwardPagination =
      urlSearchParams.page != null &&
      request.pageNo - urlSearchParams.page === 1 &&
      request.sorting === urlSearchParams.sorting;
    request.requestFullResult = !isForwardPagination;
    currentPage.value = request.pageNo ?? 1;
    sorting.value = request.sorting;
    await useAsyncSpinner(async () => {
      response.value = await ProductApi.categorySearch(request);
      if (isForwardPagination) {
        products.value.push(...response.value.tiles);
      } else {
        hideInitialProducts.value = true;
        products.value = response.value.tiles;
      }
      updateFilters(response.value.facets);
      remainingProducts.value = response.value.remainingProducts;
      totalHits.value = response.value.totalHits;
      loading.value = false;
      urlSearchParams = createUrlSearchParams(location.href);
    });
  } else {
    // Initial page
    hideInitialProducts.value = false;
    products.value.length = 0;
    remainingProducts.value = props.remainingProducts;
    currentPage.value = 1;
  }
});

function toggleElementDisplayOnMounted(): void {
  const selectors = [
    ".product-listings-container",
    ".category__footer-buttons",
    ".filters__top-container",
    ".filter-tags",
    ".filters__mobile-menu",
    ".loading",
  ];

  selectors.forEach((selector) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      if (selector === ".loading") {
        element.classList.add("d-none");
      } else {
        element.classList.remove("d-none", "invisible");
      }
    }
  });
}

return (_ctx: any,_cache: any) => {
  return _renderSlot(_ctx.$slots, "default", {
    isMd: _unref(isMd),
    window2: window2.value,
    onMoveToTopClick: onMoveToTopClick,
    showMore: showMore,
    products: products.value,
    remainingProducts: remainingProducts.value,
    onCartClick: onCartClick,
    onFavoriteClick: onFavoriteClick,
    isFavorited: isFavorited,
    isFilterOpen: isFilterOpen.value,
    onOpenFilterClick: onOpenFilterClick,
    onToggleFilterClick: onToggleFilterClick,
    onRemoveFilterClick: onRemoveFilterClick,
    filters: filters.value,
    onRemoveAllFiltersClick: onRemoveAllFiltersClick,
    isFilterCategoryOpen: isFilterCategoryOpen.value,
    onToggleShowAllFilterCategory: onToggleShowAllFilterCategory,
    categoryReadMoreHeight: categoryReadMoreHeight.value,
    onToggleCategoryReadMore: onToggleCategoryReadMore,
    sorting: sorting.value,
    hideInitialProducts: hideInitialProducts.value,
    onSortChange: onSortChange,
    totalHits: totalHits.value,
    filtersModalAccordionId: filtersModalAccordionId.value,
    filtersModalAccordionHeight: filtersModalAccordionHeight.value,
    onToggleFilterAccordion: onToggleFilterAccordion,
    onFiltersMenuCloseClick: onFiltersMenuCloseClick,
    onFiltersMenuOpenClick: onFiltersMenuOpenClick,
    isFiltersMenuOpen: isFiltersMenuOpen.value,
    isSortOpen: isSortOpen.value,
    onOpenSortClick: onOpenSortClick,
    onCloseSortClick: onCloseSortClick,
    onToggleSortClick: onToggleSortClick,
    onSortChangeDesktop: onSortChangeDesktop,
    onFiltersMenuOpenClick2: onFiltersMenuOpenClick2,
    isSortOpen2: isSortOpen2.value,
    onFiltersModalClick: onFiltersModalClick,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
    onToggleDisplayMode: toggleDisplayMode,
    availableFilters: __props.availableFilters
  })
}
}

})